import { AssetBase } from './Asset';
import { LocaleCode } from '../../../../../utils/Locale';
import type { ResourceLibrarySitePageBaseItem } from './resourceLibrarySitePageItems';

export interface ResourceLibraryItemBase {
    id: string;
    /** System name of the resource library item, URL friendly */
    key: string;
    /** Localized name of the resource library item */
    name: string;
    /** Localized description of the resource library item */
    description: string;
    /** ResourceType.id */
    resourceTypeId: string;
    /** Brand.id */
    brandId: string | null;
    /** List of ProductGroup.id */
    productGroupIds: string[];
    /** List of ProductApplication.id */
    productApplicationIds: string[];
    /**
     * List of NavigationNode ids. Used to filter items in pre-filtered lists. Not a RL category.
     */
    navigationScopeIds: string[];
    /**
     * List of NavigationNode ids. Items assigned to at least one node are visible only in RL lists
     * that use the 'navigationScopeIds' filter.
     */
    exclusiveNavigationScopeIds: string[];
    /** Relative to the Pimcore server base URL (PIMCORE_CONTENT_BASE_URL from utils/Pimcore.ts) */
    thumbnailUrl: string | null;
    publicationDate?: string | null;
    expirationDate?: string | null; // Dates can be nullable
    validFromDate?: string | null;
    lastModificationDate?: string | null;
}

export const RESOURCE_LIBRARY_ITEM_TYPES = [
    'asset',
    'internalLink',
    'news',
    'newProductIntroduction',
    'article',
    'sitePage',
] as const;
export type ResourceLibraryItemType = (typeof RESOURCE_LIBRARY_ITEM_TYPES)[number];

export interface ResourceLibraryAssetItem extends ResourceLibraryItemBase {
    kind: Extract<ResourceLibraryItemType, 'asset'>;
    asset: AssetBase | null; // Default asset for current language
    assetByLanguage: Record<LocaleCode, AssetBase | null>;
}

export interface ResourceLibraryInternalLinkItem extends ResourceLibraryItemBase {
    kind: Extract<ResourceLibraryItemType, 'internalLink'>;
    route: string;
}

// Base News item for resource library lists and landing page news cards (does not have 'body' field)
export interface ResourceLibraryNewsBaseItem extends ResourceLibraryItemBase {
    kind: Extract<ResourceLibraryItemType, 'news'>;
    coverImageUrl: string | null;
}

export interface ResourceLibraryNewsItem extends ResourceLibraryNewsBaseItem {
    body: string | null;
}

// Base NewProductIntroduction item for resource library lists
export interface ResourceLibraryNewProductIntroductionBaseItem extends ResourceLibraryItemBase {
    kind: Extract<ResourceLibraryItemType, 'newProductIntroduction'>;
    coverImageUrl: string;
}

export interface ResourceLibraryNewProductIntroductionItem
    extends ResourceLibraryNewProductIntroductionBaseItem {
    /** Headline with <link-to-brief></link-to-brief> placeholder that should be replaced with a link to the product brief */
    headline: string;
    /** Few playntext paragraphs */
    body: string;
    /** Body plaintext paragraphs split into an array */
    bodyParagraphs: string[];
    /** List of plaintext key facts used as bullet points */
    quickFacts: string[];
    /** List of plaintext key features used as bullet points */
    features: string[];
    /** List of plaintext customer values used as bullet points */
    customerValues: string[];
    /** List of plaintext applications used as bullet points */
    targetApplications: string[];
    /** List of plaintext market advantages used as bullet points */
    marketAdvantages: string[];
    /** Summary paragraph (without static "contact as" text) */
    closingSummary: string;
    /** Product brief reference to any RL item */
    productBrief: {
        id: string;
        kind: ResourceLibraryItemType;
    };
    /** Route to the similar product search page, usually /products/... */
    similarProductsRoute: string;
}

// Base Article item for resource library lists (does not have content sections and additional bricks)
export interface ResourceLibraryArticleBaseItem extends ResourceLibraryItemBase {
    kind: Extract<ResourceLibraryItemType, 'article'>;
}

export interface ResourceLibraryArticleItemContentSection {
    level: number;
    title: string | null;
    content: string | null;
}

export interface ResourceLibraryArticleItem extends ResourceLibraryArticleBaseItem {
    coverImageUrl: string | null;
    contentSections: ResourceLibraryArticleItemContentSection[];
    navigationNodeId: string | null;
}

export type ResourceLibraryItem =
    | ResourceLibraryAssetItem
    | ResourceLibraryInternalLinkItem
    | ResourceLibraryArticleBaseItem
    | ResourceLibraryNewsBaseItem
    | ResourceLibraryNewProductIntroductionBaseItem
    | ResourceLibrarySitePageBaseItem;

// Union type for "What's New" style lists combining News and New Product Introductions
export type ResourceLibraryNewsUnionBaseItem =
    | ResourceLibraryNewsBaseItem
    | ResourceLibraryNewProductIntroductionBaseItem
    | ResourceLibrarySitePageBaseItem;

// Predicate functctions

export function isAssetItem(item: ResourceLibraryItem): item is ResourceLibraryAssetItem {
    return item.kind === 'asset';
}

export function isInternalLinkItem(
    item: ResourceLibraryItem
): item is ResourceLibraryInternalLinkItem {
    return item.kind === 'internalLink';
}

export function isArticleItem(item: ResourceLibraryItem): item is ResourceLibraryArticleBaseItem {
    return item.kind === 'article';
}

export function isNewsItem(item: ResourceLibraryItem): item is ResourceLibraryNewsBaseItem {
    return item.kind === 'news';
}

export function isNewProductIntroductionItem(
    item: ResourceLibraryItem
): item is ResourceLibraryNewProductIntroductionBaseItem {
    return item.kind === 'newProductIntroduction';
}

export function isSitePageItem(item: ResourceLibraryItem): item is ResourceLibrarySitePageBaseItem {
    return item.kind === 'sitePage';
}
